// store/slices/turnaroundDetailSlice.ts
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import TurnAroundService from '@/services/turnarounds';
import { Turnaround } from '@/types/turnaround';

interface TurnaroundDetailItem {
  data: Turnaround | null;
  loading: boolean;
  error: string | null;
  fetched: boolean;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
}

interface TurnaroundDetailState {
  details: {
    [turnaroundId: string]: TurnaroundDetailItem;
  };
}

const initialState: TurnaroundDetailState = {
  details: {},
};

// Async thunk for fetching turnaround detail by ID
export const fetchTurnaroundDetail = createAsyncThunk<
  Turnaround,
  { turnaroundId: string; accessToken?: string }
>(
  'turnaroundDetail/fetchTurnaroundDetail',
  async ({ turnaroundId, accessToken }) => {
    const turnAroundService = new TurnAroundService(undefined, accessToken);
    const apiResponse = await turnAroundService.getTurnaroundById(turnaroundId);

    // Normalize the response
    apiResponse.id = apiResponse.turnaround_id;
    apiResponse.location = apiResponse.location_name;
    
    return apiResponse;
  }
);

const turnaroundDetailSlice = createSlice({
  name: 'turnaroundDetail',
  initialState,
  reducers: {
    clearTurnaroundDetail: (state, action) => {
      const turnaroundId = action.payload;
      if (turnaroundId && state.details[turnaroundId]) {
        delete state.details[turnaroundId];
      } else {
        state.details = {};
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTurnaroundDetail.pending, (state, action) => {
        const turnaroundId = action.meta.arg.turnaroundId;
        if (!state.details[turnaroundId]) {
          state.details[turnaroundId] = {
            data: null,
            loading: false,
            error: null,
            fetched: false,
            status: 'idle',
          };
        }
        state.details[turnaroundId].loading = true;
        state.details[turnaroundId].error = null;
        state.details[turnaroundId].status = 'loading';
      })
      .addCase(fetchTurnaroundDetail.fulfilled, (state, action) => {
        const turnaroundId = action.meta.arg.turnaroundId;
        state.details[turnaroundId].loading = false;
        state.details[turnaroundId].data = action.payload;
        state.details[turnaroundId].error = null;
        state.details[turnaroundId].fetched = true;
        state.details[turnaroundId].status = 'succeeded';
      })
      .addCase(fetchTurnaroundDetail.rejected, (state, action) => {
        const turnaroundId = action.meta.arg.turnaroundId;
        if (!state.details[turnaroundId]) {
          state.details[turnaroundId] = {
            data: null,
            loading: false,
            error: null,
            fetched: false,
            status: 'idle',
          };
        }
        state.details[turnaroundId].loading = false;
        state.details[turnaroundId].error = action.error.message || 'Failed to load turnaround details';
        state.details[turnaroundId].data = null;
        state.details[turnaroundId].fetched = true;
        state.details[turnaroundId].status = 'failed';
      });
  },
});

export const { clearTurnaroundDetail } = turnaroundDetailSlice.actions;
export default turnaroundDetailSlice.reducer;
